import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Divider,
  Alert,
  AlertTitle,
  CircularProgress,
  Collapse,
  Stack,
  Switch,
  FormControlLabel,
  Chip,
  IconButton,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Description,
  Save,
  Person,
  AttachMoney,
  Percent,
  ExpandMore,
  ExpandLess,
  Add,
  Delete,
  CheckCircle,
  ContentCopy,
  Download,
  Pix,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { api } from '@/services/api';
import toast from 'react-hot-toast';
import { format, addDays } from 'date-fns';

const ESTADOS_BR = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const ESPECIE_DOCUMENTO = [
  { value: 'DM', label: 'Duplicata Mercantil' },
  { value: 'DS', label: 'Duplicata de Serviço' },
  { value: 'NP', label: 'Nota Promissória' },
  { value: 'RC', label: 'Recibo' },
  { value: 'ME', label: 'Mensalidade Escolar' },
  { value: 'CH', label: 'Cheque' },
  { value: 'DD', label: 'Documento de Dívida' },
  { value: 'OU', label: 'Outros' },
];

const MODALIDADES = [
  { value: 1, label: 'Simples com Registro' },
  { value: 3, label: 'Caucionada' },
  { value: 4, label: 'Vinculada' },
  { value: 5, label: 'Carnê de Pagamentos' },
  { value: 8, label: 'Cobrança Conta Capital' },
];

const TIPO_MULTA = [
  { value: 0, label: 'Sem multa' },
  { value: 1, label: 'Valor fixo' },
  { value: 2, label: 'Percentual' },
];

const TIPO_JUROS = [
  { value: 0, label: 'Sem juros' },
  { value: 1, label: 'Valor por dia' },
  { value: 2, label: 'Taxa mensal' },
  { value: 3, label: 'Isento' },
];

const TIPO_DESCONTO = [
  { value: 0, label: 'Sem desconto' },
  { value: 1, label: 'Valor fixo até data' },
  { value: 2, label: 'Percentual até data' },
  { value: 3, label: 'Valor por antecipação (dia corrido)' },
  { value: 4, label: 'Valor por antecipação (dia útil)' },
  { value: 5, label: 'Percentual por antecipação (dia corrido)' },
  { value: 6, label: 'Percentual por antecipação (dia útil)' },
];

const PROTESTO_OPTIONS = [
  { value: 0, label: 'Sem protesto' },
  { value: 1, label: 'Protestar dias corridos' },
  { value: 2, label: 'Protestar dias úteis' },
  { value: 3, label: 'Não protestar' },
];

const NEGATIVACAO_OPTIONS = [
  { value: 0, label: 'Sem negativação' },
  { value: 2, label: 'Negativar dias corridos' },
  { value: 3, label: 'Não negativar' },
];

interface BoletoResponse {
  nossoNumero: number;
  seuNumero: string;
  codigoBarras: string;
  linhaDigitavel: string;
  valor: number;
  dataVencimento: string;
  pdfBoleto?: string;
  qrCode?: string;
  pagador: {
    nome: string;
    numeroCpfCnpj: string;
  };
}

const BoletoNewPage: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [generatedBoleto, setGeneratedBoleto] = useState<BoletoResponse | null>(null);
  const [copied, setCopied] = useState(false);

  // Basic fields
  const [valor, setValor] = useState('');
  const [dataVencimento, setDataVencimento] = useState<Date | null>(addDays(new Date(), 30));
  const [dataLimitePagamento, setDataLimitePagamento] = useState<Date | null>(null);
  const [codigoModalidade, setCodigoModalidade] = useState(1);
  const [codigoEspecieDocumento, setCodigoEspecieDocumento] = useState('DM');
  const [seuNumero, setSeuNumero] = useState('');

  // Pagador (payer)
  const [pagadorNome, setPagadorNome] = useState('');
  const [pagadorDocType, setPagadorDocType] = useState<'cpf' | 'cnpj'>('cpf');
  const [pagadorDoc, setPagadorDoc] = useState('');
  const [pagadorEmail, setPagadorEmail] = useState('');
  const [pagadorEndereco, setPagadorEndereco] = useState('');
  const [pagadorBairro, setPagadorBairro] = useState('');
  const [pagadorCidade, setPagadorCidade] = useState('');
  const [pagadorUf, setPagadorUf] = useState('');
  const [pagadorCep, setPagadorCep] = useState('');

  // Multa (fine)
  const [tipoMulta, setTipoMulta] = useState(0);
  const [valorMulta, setValorMulta] = useState('');
  const [dataMulta, setDataMulta] = useState<Date | null>(null);

  // Juros (interest)
  const [tipoJurosMora, setTipoJurosMora] = useState(0);
  const [valorJurosMora, setValorJurosMora] = useState('');
  const [dataJurosMora, setDataJurosMora] = useState<Date | null>(null);

  // Desconto
  const [tipoDesconto, setTipoDesconto] = useState(0);
  const [dataPrimeiroDesconto, setDataPrimeiroDesconto] = useState<Date | null>(null);
  const [valorPrimeiroDesconto, setValorPrimeiroDesconto] = useState('');
  const [dataSegundoDesconto, setDataSegundoDesconto] = useState<Date | null>(null);
  const [valorSegundoDesconto, setValorSegundoDesconto] = useState('');
  const [dataTerceiroDesconto, setDataTerceiroDesconto] = useState<Date | null>(null);
  const [valorTerceiroDesconto, setValorTerceiroDesconto] = useState('');

  // Protesto/Negativação
  const [codigoProtesto, setCodigoProtesto] = useState(0);
  const [numeroDiasProtesto, setNumeroDiasProtesto] = useState('');
  const [codigoNegativacao, setCodigoNegativacao] = useState(0);
  const [numeroDiasNegativacao, setNumeroDiasNegativacao] = useState('');

  // PIX
  const [habilitarPix, setHabilitarPix] = useState(true);

  // Mensagens
  const [showMensagens, setShowMensagens] = useState(false);
  const [mensagens, setMensagens] = useState<string[]>([]);
  const [novaMensagem, setNovaMensagem] = useState('');

  // Sections visibility
  const [showEncargos, setShowEncargos] = useState(false);
  const [showDesconto, setShowDesconto] = useState(false);
  const [showProtesto, setShowProtesto] = useState(false);

  const handleAddMensagem = () => {
    if (novaMensagem.trim() && mensagens.length < 5) {
      setMensagens([...mensagens, novaMensagem.trim()]);
      setNovaMensagem('');
    }
  };

  const handleRemoveMensagem = (index: number) => {
    setMensagens(mensagens.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!valor || parseFloat(valor) <= 0) {
      toast.error('Informe um valor válido');
      return;
    }

    if (!dataVencimento) {
      toast.error('Informe a data de vencimento');
      return;
    }

    if (!pagadorNome || !pagadorDoc) {
      toast.error('Dados do pagador são obrigatórios');
      return;
    }

    setLoading(true);
    setGeneratedBoleto(null);

    try {
      const boletoData: any = {
        valor: parseFloat(valor),
        dataVencimento: format(dataVencimento, 'yyyy-MM-dd'),
        codigoModalidade,
        codigoEspecieDocumento,
        pagador: {
          nome: pagadorNome,
          numeroCpfCnpj: pagadorDoc,
          endereco: pagadorEndereco,
          bairro: pagadorBairro,
          cidade: pagadorCidade,
          uf: pagadorUf,
          cep: pagadorCep,
          email: pagadorEmail,
        },
        gerarPdf: true,
      };

      if (seuNumero) boletoData.seuNumero = seuNumero;
      if (dataLimitePagamento) {
        boletoData.dataLimitePagamento = format(dataLimitePagamento, 'yyyy-MM-dd');
      }

      // Multa
      if (tipoMulta > 0 && valorMulta) {
        boletoData.tipoMulta = tipoMulta;
        boletoData.valorMulta = parseFloat(valorMulta);
        if (dataMulta) {
          boletoData.dataMulta = format(dataMulta, 'yyyy-MM-dd');
        }
      }

      // Juros
      if (tipoJurosMora > 0 && valorJurosMora) {
        boletoData.tipoJurosMora = tipoJurosMora;
        boletoData.valorJurosMora = parseFloat(valorJurosMora);
        if (dataJurosMora) {
          boletoData.dataJurosMora = format(dataJurosMora, 'yyyy-MM-dd');
        }
      }

      // Desconto
      if (tipoDesconto > 0) {
        boletoData.tipoDesconto = tipoDesconto;
        if (dataPrimeiroDesconto && valorPrimeiroDesconto) {
          boletoData.dataPrimeiroDesconto = format(dataPrimeiroDesconto, 'yyyy-MM-dd');
          boletoData.valorPrimeiroDesconto = parseFloat(valorPrimeiroDesconto);
        }
        if (dataSegundoDesconto && valorSegundoDesconto) {
          boletoData.dataSegundoDesconto = format(dataSegundoDesconto, 'yyyy-MM-dd');
          boletoData.valorSegundoDesconto = parseFloat(valorSegundoDesconto);
        }
        if (dataTerceiroDesconto && valorTerceiroDesconto) {
          boletoData.dataTerceiroDesconto = format(dataTerceiroDesconto, 'yyyy-MM-dd');
          boletoData.valorTerceiroDesconto = parseFloat(valorTerceiroDesconto);
        }
      }

      // Protesto
      if (codigoProtesto > 0 && codigoProtesto !== 3) {
        boletoData.codigoProtesto = codigoProtesto;
        if (numeroDiasProtesto) {
          boletoData.numeroDiasProtesto = parseInt(numeroDiasProtesto);
        }
      }

      // Negativação
      if (codigoNegativacao > 0 && codigoNegativacao !== 3) {
        boletoData.codigoNegativacao = codigoNegativacao;
        if (numeroDiasNegativacao) {
          boletoData.numeroDiasNegativacao = parseInt(numeroDiasNegativacao);
        }
      }

      // PIX
      boletoData.codigoCadastrarPIX = habilitarPix ? 1 : 0;

      // Mensagens
      if (mensagens.length > 0) {
        boletoData.mensagensInstrucao = mensagens;
      }

      const response = await api.post('/boletos', boletoData);

      if (response?.success) {
        setGeneratedBoleto(response.data);
        toast.success('Boleto gerado com sucesso!');
      } else {
        toast.error(response?.error?.message || 'Erro ao gerar boleto');
      }
    } catch (err: any) {
      console.error('Error creating boleto:', err);
      toast.error(err.message || 'Erro ao gerar boleto');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLinhaDigitavel = () => {
    if (generatedBoleto?.linhaDigitavel) {
      navigator.clipboard.writeText(generatedBoleto.linhaDigitavel);
      setCopied(true);
      toast.success('Linha digitável copiada!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleDownloadPdf = () => {
    if (generatedBoleto?.pdfBoleto) {
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${generatedBoleto.pdfBoleto}`;
      link.download = `boleto-${generatedBoleto.nossoNumero}.pdf`;
      link.click();
    }
  };

  const handleNewBoleto = () => {
    setGeneratedBoleto(null);
    setValor('');
    setDataVencimento(addDays(new Date(), 30));
    setPagadorNome('');
    setPagadorDoc('');
    setPagadorEmail('');
    setPagadorEndereco('');
    setPagadorBairro('');
    setPagadorCidade('');
    setPagadorUf('');
    setPagadorCep('');
    setSeuNumero('');
    setMensagens([]);
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  if (generatedBoleto) {
    return (
      <Box>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
          <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
          Boleto Gerado
        </Typography>

        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
          <AlertTitle>Boleto criado com sucesso!</AlertTitle>
          Nosso Número: {generatedBoleto.nossoNumero}
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Dados do Boleto
              </Typography>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Nosso Número</Typography>
                  <Typography variant="body2" fontFamily="monospace">{generatedBoleto.nossoNumero}</Typography>
                </Box>
                {generatedBoleto.seuNumero && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Seu Número</Typography>
                    <Typography variant="body2">{generatedBoleto.seuNumero}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Valor</Typography>
                  <Typography variant="h6" color="primary">{formatCurrency(generatedBoleto.valor)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Vencimento</Typography>
                  <Typography variant="body2">{new Date(generatedBoleto.dataVencimento).toLocaleDateString('pt-BR')}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Pagador</Typography>
                  <Typography variant="body2">{generatedBoleto.pagador?.nome}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {generatedBoleto.pagador?.numeroCpfCnpj?.length === 11 ? 'CPF: ' : 'CNPJ: '}
                    {generatedBoleto.pagador?.numeroCpfCnpj}
                  </Typography>
                </Box>
                {generatedBoleto.qrCode && (
                  <Box>
                    <Chip icon={<Pix />} label="PIX habilitado" color="success" size="small" />
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                Linha Digitável
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: alpha(theme.palette.grey[500], 0.08),
                  fontFamily: 'monospace',
                  fontSize: '0.9rem',
                  wordBreak: 'break-all',
                }}
              >
                {generatedBoleto.linhaDigitavel}
              </Paper>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                  onClick={handleCopyLinhaDigitavel}
                  color={copied ? 'success' : 'primary'}
                >
                  {copied ? 'Copiado!' : 'Copiar'}
                </Button>
                {generatedBoleto.pdfBoleto && (
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleDownloadPdf}
                  >
                    Baixar PDF
                  </Button>
                )}
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Código de Barras
              </Typography>
              <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                {generatedBoleto.codigoBarras}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Button variant="outlined" startIcon={<Add />} onClick={handleNewBoleto}>
              Novo Boleto
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        <Description sx={{ mr: 1, verticalAlign: 'middle' }} />
        Emitir Boleto
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          {/* Pagador */}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <Person sx={{ mr: 1 }} />
            Dados do Pagador
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome/Razão Social *"
                value={pagadorNome}
                onChange={(e) => setPagadorNome(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction="row" spacing={1}>
                <FormControl sx={{ minWidth: 100 }}>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={pagadorDocType}
                    label="Tipo"
                    onChange={(e) => setPagadorDocType(e.target.value as 'cpf' | 'cnpj')}
                  >
                    <MenuItem value="cpf">CPF</MenuItem>
                    <MenuItem value="cnpj">CNPJ</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label={pagadorDocType === 'cpf' ? 'CPF *' : 'CNPJ *'}
                  value={pagadorDoc}
                  onChange={(e) => setPagadorDoc(e.target.value.replace(/\D/g, ''))}
                  required
                  inputProps={{ maxLength: pagadorDocType === 'cpf' ? 11 : 14 }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="E-mail"
                type="email"
                value={pagadorEmail}
                onChange={(e) => setPagadorEmail(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Endereço"
                value={pagadorEndereco}
                onChange={(e) => setPagadorEndereco(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Bairro"
                value={pagadorBairro}
                onChange={(e) => setPagadorBairro(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Cidade"
                value={pagadorCidade}
                onChange={(e) => setPagadorCidade(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <FormControl fullWidth>
                <InputLabel>UF</InputLabel>
                <Select value={pagadorUf} label="UF" onChange={(e) => setPagadorUf(e.target.value)}>
                  <MenuItem value="">-</MenuItem>
                  {ESTADOS_BR.map((uf) => (
                    <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                label="CEP"
                value={pagadorCep}
                onChange={(e) => setPagadorCep(e.target.value.replace(/\D/g, ''))}
                inputProps={{ maxLength: 8 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Dados do Boleto */}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <AttachMoney sx={{ mr: 1 }} />
            Dados do Boleto
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Valor *"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
                type="number"
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Data de Vencimento *"
                value={dataVencimento}
                onChange={(newValue) => setDataVencimento(newValue)}
                minDate={new Date()}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Data Limite Pagamento"
                value={dataLimitePagamento}
                onChange={(newValue) => setDataLimitePagamento(newValue)}
                minDate={dataVencimento || new Date()}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Modalidade</InputLabel>
                <Select
                  value={codigoModalidade}
                  label="Modalidade"
                  onChange={(e) => setCodigoModalidade(e.target.value as number)}
                >
                  {MODALIDADES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Espécie Documento</InputLabel>
                <Select
                  value={codigoEspecieDocumento}
                  label="Espécie Documento"
                  onChange={(e) => setCodigoEspecieDocumento(e.target.value)}
                >
                  {ESPECIE_DOCUMENTO.map((e) => (
                    <MenuItem key={e.value} value={e.value}>{e.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Seu Número (referência)"
                value={seuNumero}
                onChange={(e) => setSeuNumero(e.target.value)}
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
          </Grid>

          {/* PIX */}
          <FormControlLabel
            control={
              <Switch
                checked={habilitarPix}
                onChange={(e) => setHabilitarPix(e.target.checked)}
              />
            }
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <Pix />
                <span>Habilitar pagamento via PIX (QR Code no boleto)</span>
              </Stack>
            }
            sx={{ mb: 2 }}
          />

          <Divider sx={{ my: 3 }} />

          {/* Encargos (Multa e Juros) */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant="text"
              startIcon={<Percent />}
              endIcon={showEncargos ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowEncargos(!showEncargos)}
            >
              Multa e Juros
            </Button>
            <Collapse in={showEncargos}>
              <Grid container spacing={2} sx={{ mt: 1, pl: 2 }}>
                {/* Multa */}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Multa por atraso</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Multa</InputLabel>
                    <Select
                      value={tipoMulta}
                      label="Tipo de Multa"
                      onChange={(e) => setTipoMulta(e.target.value as number)}
                    >
                      {TIPO_MULTA.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {tipoMulta > 0 && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label={tipoMulta === 1 ? 'Valor (R$)' : 'Percentual (%)'}
                        value={valorMulta}
                        onChange={(e) => setValorMulta(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: tipoMulta === 1 ? <InputAdornment position="start">R$</InputAdornment> : null,
                          endAdornment: tipoMulta === 2 ? <InputAdornment position="end">%</InputAdornment> : null,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="Data da multa"
                        value={dataMulta}
                        onChange={(newValue) => setDataMulta(newValue)}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                  </>
                )}

                {/* Juros */}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Juros de mora</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Juros</InputLabel>
                    <Select
                      value={tipoJurosMora}
                      label="Tipo de Juros"
                      onChange={(e) => setTipoJurosMora(e.target.value as number)}
                    >
                      {TIPO_JUROS.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {tipoJurosMora > 0 && tipoJurosMora !== 3 && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label={tipoJurosMora === 1 ? 'Valor por dia (R$)' : 'Taxa mensal (%)'}
                        value={valorJurosMora}
                        onChange={(e) => setValorJurosMora(e.target.value)}
                        type="number"
                        InputProps={{
                          startAdornment: tipoJurosMora === 1 ? <InputAdornment position="start">R$</InputAdornment> : null,
                          endAdornment: tipoJurosMora === 2 ? <InputAdornment position="end">%</InputAdornment> : null,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <DatePicker
                        label="Data início juros"
                        value={dataJurosMora}
                        onChange={(newValue) => setDataJurosMora(newValue)}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Collapse>
          </Box>

          {/* Desconto */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant="text"
              startIcon={<AttachMoney />}
              endIcon={showDesconto ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowDesconto(!showDesconto)}
            >
              Desconto
            </Button>
            <Collapse in={showDesconto}>
              <Grid container spacing={2} sx={{ mt: 1, pl: 2 }}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Desconto</InputLabel>
                    <Select
                      value={tipoDesconto}
                      label="Tipo de Desconto"
                      onChange={(e) => setTipoDesconto(e.target.value as number)}
                    >
                      {TIPO_DESCONTO.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {tipoDesconto > 0 && [1, 2].includes(tipoDesconto) && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Primeiro desconto
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <DatePicker
                        label="Data 1º desconto"
                        value={dataPrimeiroDesconto}
                        onChange={(newValue) => setDataPrimeiroDesconto(newValue)}
                        maxDate={dataVencimento || undefined}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label={tipoDesconto === 1 ? 'Valor (R$)' : 'Percentual (%)'}
                        value={valorPrimeiroDesconto}
                        onChange={(e) => setValorPrimeiroDesconto(e.target.value)}
                        type="number"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Segundo desconto (opcional)
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <DatePicker
                        label="Data 2º desconto"
                        value={dataSegundoDesconto}
                        onChange={(newValue) => setDataSegundoDesconto(newValue)}
                        maxDate={dataVencimento || undefined}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label={tipoDesconto === 1 ? 'Valor (R$)' : 'Percentual (%)'}
                        value={valorSegundoDesconto}
                        onChange={(e) => setValorSegundoDesconto(e.target.value)}
                        type="number"
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        Terceiro desconto (opcional)
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <DatePicker
                        label="Data 3º desconto"
                        value={dataTerceiroDesconto}
                        onChange={(newValue) => setDataTerceiroDesconto(newValue)}
                        maxDate={dataVencimento || undefined}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label={tipoDesconto === 1 ? 'Valor (R$)' : 'Percentual (%)'}
                        value={valorTerceiroDesconto}
                        onChange={(e) => setValorTerceiroDesconto(e.target.value)}
                        type="number"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Collapse>
          </Box>

          {/* Protesto/Negativação */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant="text"
              endIcon={showProtesto ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowProtesto(!showProtesto)}
            >
              Protesto / Negativação
            </Button>
            <Collapse in={showProtesto}>
              <Grid container spacing={2} sx={{ mt: 1, pl: 2 }}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Protesto</InputLabel>
                    <Select
                      value={codigoProtesto}
                      label="Protesto"
                      onChange={(e) => setCodigoProtesto(e.target.value as number)}
                    >
                      {PROTESTO_OPTIONS.map((p) => (
                        <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {codigoProtesto > 0 && codigoProtesto !== 3 && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Dias para protesto"
                      value={numeroDiasProtesto}
                      onChange={(e) => setNumeroDiasProtesto(e.target.value)}
                      type="number"
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Negativação</InputLabel>
                    <Select
                      value={codigoNegativacao}
                      label="Negativação"
                      onChange={(e) => setCodigoNegativacao(e.target.value as number)}
                    >
                      {NEGATIVACAO_OPTIONS.map((n) => (
                        <MenuItem key={n.value} value={n.value}>{n.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {codigoNegativacao === 2 && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Dias para negativação"
                      value={numeroDiasNegativacao}
                      onChange={(e) => setNumeroDiasNegativacao(e.target.value)}
                      type="number"
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                )}
              </Grid>
            </Collapse>
          </Box>

          {/* Mensagens */}
          <Box sx={{ mb: 3 }}>
            <Button
              variant="text"
              endIcon={showMensagens ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setShowMensagens(!showMensagens)}
            >
              Instruções ao Caixa (até 5)
            </Button>
            <Collapse in={showMensagens}>
              <Stack spacing={1} sx={{ mt: 1, pl: 2 }}>
                {mensagens.map((msg, idx) => (
                  <Chip
                    key={idx}
                    label={msg}
                    onDelete={() => handleRemoveMensagem(idx)}
                    size="small"
                  />
                ))}
                {mensagens.length < 5 && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      fullWidth
                      size="small"
                      label="Nova instrução"
                      value={novaMensagem}
                      onChange={(e) => setNovaMensagem(e.target.value)}
                      inputProps={{ maxLength: 100 }}
                    />
                    <IconButton
                      color="primary"
                      onClick={handleAddMensagem}
                      disabled={!novaMensagem.trim()}
                    >
                      <Add />
                    </IconButton>
                  </Stack>
                )}
              </Stack>
            </Collapse>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
            disabled={loading}
          >
            {loading ? 'Gerando...' : 'Gerar Boleto'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default BoletoNewPage;
